import { clsx } from 'clsx';
import { PropsWithChildren } from 'react';
import { Icon } from '../icon/Icon';
import { useMemoizedDataIds } from '../shared/utils/dataId';
import { IconButton } from '../button/IconButton/IconButton';
import styles from './Banner.module.scss';
import { BANNER_COLORS } from './constants';
import { Button, ButtonProps } from '../button/Button';
import { BANNER_DATA_IDS } from './dataIds';

export interface BannerProps {
  open: boolean;
  dataId: string;
  className?: string;
  icon?: string;
  iconClass?: string;
  canClose?: boolean;
  color: (typeof BANNER_COLORS)[number];
  onClose?: VoidFunction;
  primaryBtnProps?: Partial<ButtonProps>;
  secondaryBtnProps?: Partial<ButtonProps>;
}

export function Banner({
  open,
  dataId,
  className = '',
  icon,
  iconClass,
  canClose,
  color,
  onClose,
  primaryBtnProps,
  secondaryBtnProps,
  children
}: PropsWithChildren<BannerProps>) {
  const dataIds = useMemoizedDataIds(BANNER_DATA_IDS, dataId);

  if (!open) {
    return null;
  }

  return (
    <div className={clsx(styles.banner, styles[color], className)} data-id={dataIds.container}>
      <div className='flex-1 d-flex align-items-start w-100'>
        {icon ? <Icon size={3} name={icon} className={clsx(iconClass, styles.icon)} /> : null}

        <div className={styles.bannerContent}>{children}</div>
      </div>

      {primaryBtnProps || secondaryBtnProps ? (
        <div className={styles.bannerActions}>
          {secondaryBtnProps ? (
            <Button
              size='sm'
              variation='outline'
              dataId={dataIds.secondaryAction}
              {...secondaryBtnProps}
              className={clsx(styles.bannerAction, secondaryBtnProps.className)}
            />
          ) : null}

          {primaryBtnProps ? (
            <Button
              size='sm'
              dataId={dataIds.primaryAction}
              {...primaryBtnProps}
              className={clsx(styles.bannerAction, primaryBtnProps.className)}
            />
          ) : null}
        </div>
      ) : null}

      {canClose ? (
        <div className={styles.closeBannerWrapper}>
          <IconButton
            dataId={dataIds.close}
            className={styles.closeBanner}
            size='small'
            label='Close'
            onClick={() => {
              if (onClose) {
                onClose();
              }
            }}
          >
            <Icon name='close' className={styles.closeIcon} size={0} />
          </IconButton>
        </div>
      ) : null}
    </div>
  );
}
